import { useEffect, useRef, useState, type ReactElement } from "react";
import { Group, Path, Rect, Text } from "react-konva";
import type { CollaboratorState } from "../../lib/sharedRoomTypes";
import type { Opening, WallMount } from "../../lib/types";
import { chromeScale } from "../../lib/canvasLabels";

/**
 * Wall-elevation specific collaborator overlay. The wall view uses
 * a custom (alongWall, fromFloor) coordinate system rather than
 * `useFitToRoom`, so it gets its own cursor + selection components.
 *
 * Cursor coords arrive in the same `(x, y)` field as the design/build
 * views, but in the wall view they encode `(alongWall, fromFloor)` —
 * both in inches.
 */

interface WallProjection {
  scale: number;
  x0: number;
  y0: number;
  /** Wall length in inches. */
  w: number;
  /** Wall ceiling height in inches. */
  h: number;
}

function projectAlong(
  fit: WallProjection,
  alongWall: number,
  fromFloor: number
): { x: number; y: number } {
  return {
    x: fit.x0 + alongWall * fit.scale,
    y: fit.y0 + fit.h * fit.scale - fromFloor * fit.scale,
  };
}

interface SelectionsProps {
  collaborators: ReadonlyMap<string, CollaboratorState>;
  mounts: WallMount[];
  openings: Opening[];
  fit: WallProjection;
  activeWallIndex: number | null;
  interiorWallId?: string | null;
  interiorSegment?: number | null;
  /** Stage zoom, so name pills keep one on-screen size. */
  stageScale?: number;
}

export function WallCollaboratorSelections({
  collaborators,
  mounts,
  openings,
  fit,
  activeWallIndex,
  interiorWallId = null,
  interiorSegment = null,
  stageScale = 1,
}: SelectionsProps) {
  if (collaborators.size === 0) return null;
  const k = chromeScale(stageScale);
  const items: ReactElement[] = [];
  for (const c of collaborators.values()) {
    if (!c.selection) continue;
    if (c.selection.mountId) {
      const m = mounts.find(
        (x) =>
          x.id === c.selection!.mountId &&
          (interiorWallId
            ? x.interiorWallId === interiorWallId && x.interiorSegment === interiorSegment
            : !x.interiorWallId && x.wallIndex === activeWallIndex)
      );
      if (m) {
        items.push(
          <RectHalo
            key={`m-${c.uid}-${m.id}`}
            collab={c}
            cx={projectAlong(fit, m.alongWall, m.fromFloor).x}
            cy={projectAlong(fit, m.alongWall, m.fromFloor).y}
            wPx={m.width * fit.scale}
            hPx={m.height * fit.scale}
            k={k}
          />
        );
      }
    }
    if (c.selection.openingId) {
      const o = openings.find(
        (x) =>
          x.id === c.selection!.openingId &&
          (interiorWallId
            ? x.interiorWallId === interiorWallId && x.interiorSegment === interiorSegment
            : !x.interiorWallId && x.wallIndex === activeWallIndex)
      );
      if (o) {
        items.push(
          <RectHalo
            key={`o-${c.uid}-${o.id}`}
            collab={c}
            cx={projectAlong(fit, o.alongWall, o.fromFloor).x}
            cy={projectAlong(fit, o.alongWall, o.fromFloor).y}
            wPx={o.width * fit.scale}
            hPx={o.height * fit.scale}
            k={k}
          />
        );
      }
    }
  }
  return <>{items}</>;
}

function RectHalo({
  collab,
  cx,
  cy,
  wPx,
  hPx,
  k,
}: {
  collab: CollaboratorState;
  cx: number;
  cy: number;
  wPx: number;
  hPx: number;
  k: number;
}) {
  return (
    <Group listening={false}>
      <Rect
        x={cx - wPx / 2 - 2}
        y={cy - hPx / 2 - 2}
        width={wPx + 4}
        height={hPx + 4}
        stroke={collab.color}
        strokeWidth={2.5}
        strokeScaleEnabled={false}
      />
      <NamePill
        x={cx - wPx / 2 - 2}
        y={cy - hPx / 2 - 22 * k}
        name={collab.displayName}
        color={collab.color}
        k={k}
      />
    </Group>
  );
}

interface CursorsProps {
  collaborators: ReadonlyMap<string, CollaboratorState>;
  fit: WallProjection;
  activeWallIndex: number | null;
  interiorWallId?: string | null;
  interiorSegment?: number | null;
  /** Stage zoom, so cursors keep one on-screen size. */
  stageScale?: number;
}

interface CursorAnim {
  target: { x: number; y: number };
  current: { x: number; y: number };
  lastChangeAt: number;
}

const INTERPOLATION_DURATION_MS = 80;

export function WallCollaboratorCursors({
  collaborators,
  fit,
  activeWallIndex,
  interiorWallId = null,
  interiorSegment = null,
  stageScale = 1,
}: CursorsProps) {
  const animRef = useRef<Map<string, CursorAnim>>(new Map());
  const [, forceRender] = useState(0);

  useEffect(() => {
    const next = new Map<string, CursorAnim>();
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    for (const [uid, c] of collaborators.entries()) {
      if (!c.cursor) continue;
      if (c.cursor.view !== "wall") continue;
      if (interiorWallId) {
        if (
          c.cursor.interiorWallId !== interiorWallId ||
          c.cursor.interiorSegment !== interiorSegment
        ) {
          continue;
        }
      } else if (c.cursor.interiorWallId || c.cursor.wallIndex !== activeWallIndex) {
        continue;
      }
      const target = { x: c.cursor.x, y: c.cursor.y };
      const prior = animRef.current.get(uid);
      next.set(uid, {
        target,
        current: prior?.current ?? target,
        lastChangeAt: now,
      });
    }
    animRef.current = next;
    forceRender((x) => x + 1);
  }, [collaborators, activeWallIndex, interiorWallId, interiorSegment]);

  useEffect(() => {
    if (animRef.current.size === 0) return;
    let raf = 0;
    function tick() {
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      let stillAnimating = false;
      for (const a of animRef.current.values()) {
        const t = Math.min(
          1,
          (now - a.lastChangeAt) / INTERPOLATION_DURATION_MS
        );
        const e = 1 - Math.pow(1 - t, 3);
        a.current = {
          x: a.current.x + (a.target.x - a.current.x) * e,
          y: a.current.y + (a.target.y - a.current.y) * e,
        };
        const dx = a.target.x - a.current.x;
        const dy = a.target.y - a.current.y;
        if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) stillAnimating = true;
      }
      forceRender((x) => x + 1);
      if (stillAnimating) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  });

  if (animRef.current.size === 0) return null;
  const items: ReactElement[] = [];
  for (const [uid, c] of collaborators.entries()) {
    if (!c.cursor) continue;
    if (c.cursor.view !== "wall") continue;
    if (c.cursor.wallIndex !== activeWallIndex) continue;
    const a = animRef.current.get(uid);
    if (!a) continue;
    const px = projectAlong(fit, a.current.x, a.current.y);
    items.push(
      <CursorMarker
        key={uid}
        x={px.x}
        y={px.y}
        color={c.color}
        name={c.displayName}
        k={chromeScale(stageScale)}
      />
    );
  }
  return <>{items}</>;
}

function CursorMarker({
  x,
  y,
  color,
  name,
  k,
}: {
  x: number;
  y: number;
  color: string;
  name: string;
  k: number;
}) {
  const arrow = "M 0 0 L 0 16 L 4 12 L 7 19 L 9 18 L 6 11 L 11 11 Z";
  const charW = 6.5;
  const padX = 6;
  const labelW = name.length * charW + padX * 2;
  return (
    <Group x={x} y={y} scaleX={k} scaleY={k} listening={false}>
      <Path
        data={arrow}
        fill={color}
        stroke="#ffffff"
        strokeWidth={1.5}
        shadowColor="#000"
        shadowBlur={3}
        shadowOpacity={0.25}
        shadowOffsetY={1}
      />
      <Group x={14} y={14}>
        <Rect
          x={0}
          y={0}
          width={labelW}
          height={18}
          fill={color}
          cornerRadius={9}
        />
        <Text
          x={padX}
          y={3}
          text={name}
          fontSize={11}
          fontStyle="600"
          fontFamily="Inter, sans-serif"
          fill="#ffffff"
        />
      </Group>
    </Group>
  );
}

function NamePill({
  x,
  y,
  name,
  color,
  k,
}: {
  x: number;
  y: number;
  name: string;
  color: string;
  k: number;
}) {
  const charW = 6.5;
  const padX = 6;
  const labelW = name.length * charW + padX * 2;
  return (
    <Group x={x} y={y} scaleX={k} scaleY={k} listening={false}>
      <Rect
        x={0}
        y={0}
        width={labelW}
        height={18}
        fill={color}
        cornerRadius={9}
      />
      <Text
        x={padX}
        y={3}
        text={name}
        fontSize={11}
        fontStyle="600"
        fontFamily="Inter, sans-serif"
        fill="#ffffff"
      />
    </Group>
  );
}
